import { useTranslation } from '@gimlite/common';
import {
  Form,
  FormField,
  FormItem,
  FormStructure,
  Input,
  WizardItemType,
} from '@gimlite/ui-v2';
import { zodResolver } from '@hookform/resolvers/zod';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Avatar } from '../../../components/avatar/avatar.component';
import { UpdateProfileWizardContext } from '../updateProfile.wizard';

export const InformationStep = observer(
  ({
    context,
    validate,
    submit,
    loading,
  }: WizardItemType.Component<UpdateProfileWizardContext>) => {
    const { t } = useTranslation();

    const FormSchema = z.object({
      avatar: z.string().trim().nullable().optional(),
      lastName: z
        .string()
        .trim()
        .min(1, { message: t('watermelon-VALIDATOR-REQUIRED') })
        .nullable()
        .optional(),
      firstName: z
        .string()
        .trim()
        .min(1, { message: t('watermelon-VALIDATOR-REQUIRED') })
        .nullable()
        .optional(),
      email: z
        .string()
        .trim()
        .email({ message: t('watermelon-VALIDATOR_EMAIL') })
        .nullable()
        .optional(),
      username: z.string().trim().nullable().optional(),
    });

    const form = useForm<UpdateProfileWizardContext>({
      resolver: zodResolver(FormSchema),
      defaultValues: {
        avatar: context.avatar || null,
        lastName: context.lastName || null,
        firstName: context.firstName || null,
        email: context.email || null,
        username: context.username || null,
      },
      mode: 'onTouched',
    });

    useEffect(() => {
      const subscription = form.watch((value) => {
        submit({
          avatar: value.avatar || null,
          lastName: value.lastName || null,
          firstName: value.firstName || null,
          email: value.email || null,
          username: value.username || null,
        });
      });

      return () => subscription.unsubscribe();
    }, [form, submit]);

    useEffect(() => {
      validate(form.formState.isValid);
    }, [form.formState.isValid, validate]);

    return (
      <FormStructure height="min" columns={2}>
        <Form {...form}>
          <FormField
            control={form.control}
            name="avatar"
            render={({ field }) => (
              <FormItem columnExtend={2}>
                <Avatar
                  size="large"
                  image={field.value || null}
                  isEditable={true}
                  firstName={context.firstName}
                  lastName={context.lastName}
                  onEdit={(base64String) => {
                    field.onChange(base64String);
                  }}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem columnExtend={1} label={t('watermelon-lastName')}>
                <Input {...field} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem columnExtend={1} label={t('watermelon-firstName')}>
                <Input {...field} />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem columnExtend={1} label={t('watermelon-email')}>
                <Input {...field} type="email" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem columnExtend={1} label={t('watermelon-username')}>
                <Input {...field} disabled={true} />
              </FormItem>
            )}
          />
        </Form>
      </FormStructure>
    );
  },
);
